import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem('ph_user');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setUser(parsed);
      } catch (e) {
        localStorage.removeItem('ph_user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    try {
      const { data } = await authAPI.login(credentials);
      const userData = data.data || data.user || data;
      // Store the complete user object including token
      localStorage.setItem('ph_user', JSON.stringify(userData));
      setUser(userData);
      toast.success('Welcome back!');
      return userData;
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || 'Login failed';
      toast.error(msg);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const { data } = await authAPI.register(userData);
      const user = data.data || data.user || data;
      localStorage.setItem('ph_user', JSON.stringify(user));
      setUser(user);
      toast.success('Account created successfully!');
      return user;
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || 'Registration failed';
      toast.error(msg);
      throw error;
    }
  };

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (e) {
      // continue logout even if API fails
    }
    localStorage.removeItem('ph_user');
    setUser(null);
    toast.success('Logged out');
  }, []);

  const refreshUser = async () => {
    try {
      const { data } = await authAPI.me();
      const userData = data.data || data.user || data;
      // Preserve the token from the stored user
      const stored = JSON.parse(localStorage.getItem('ph_user') || '{}');
      const merged = { ...userData, token: stored.token || userData.token };
      localStorage.setItem('ph_user', JSON.stringify(merged));
      setUser(merged);
      return merged;
    } catch (error) {
      // If refresh fails, don't logout automatically
      console.error('Failed to refresh user:', error);
    }
  };

  // Role helpers
  const hasRole = (roleName) => {
    if (!user || !user.roles) return false;
    return user.roles.some((r) => (typeof r === 'string' ? r : r.name) === roleName);
  };

  const isAdmin = hasRole('admin');
  const isRecruiter = hasRole('recruiter');
  const isCandidate = hasRole('candidate');

  const hasPermission = (permName) => {
    if (!user) return false;
    if (isAdmin) return true;
    if (!user.permissions && !user.roles) return false;
    // Check direct permissions
    if (user.permissions) {
      return user.permissions.some((p) => (typeof p === 'string' ? p : p.name) === permName);
    }
    return false;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        refreshUser,
        hasRole,
        hasPermission,
        isAdmin,
        isRecruiter,
        isCandidate,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
